import { useRyftManagementAccount } from "~/hooks/api/v2/ryftManagement/useRyftManagementAccount";
import { useMemo } from "react";
import { RyftNonHostedOnboardingContext } from "./RyftNonHostedOnboardingProvider.shared";
import { RyftAccountGetResponseType } from "@sellar-io/api-v2/types";

export const RyftNonHostedOnboardingProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const ryftAccount = useRyftManagementAccount();

  const value: RyftNonHostedOnboardingContext = useMemo(() => {
    return {
      account: ryftAccount.data,
      error: ryftAccount.error,
      onboardingSteps: mapRyftAccountToOnboardingSteps({
        account: ryftAccount.data,
      }),
      isLoading: ryftAccount.isLoading,
    };
  }, [ryftAccount.data, ryftAccount.isLoading, ryftAccount.error]);

  return (
    <RyftNonHostedOnboardingContext.Provider value={value}>
      {children}
    </RyftNonHostedOnboardingContext.Provider>
  );
};

const mapRyftAccountToOnboardingSteps = ({
  account,
}: {
  account: RyftAccountGetResponseType | undefined;
}): RyftNonHostedOnboardingContext["onboardingSteps"] => {
  if (!account) {
    return {
      "create-business": "current",
      "add-business-director": "upcoming",
      "add-bank-account": "upcoming",
      "accept-terms": "upcoming",
    };
  }

  const directorIsRequired = account.directors.length <= 0;

  if (directorIsRequired) {
    return {
      "create-business": "completed",
      "add-business-director": "current",
      "add-bank-account": "upcoming",
      "accept-terms": "upcoming",
    };
  }

  const payoutMethodIsRequired =
    account.payoutMethods.filter((method) => method.status === "Valid")
      .length === 0;

  if (payoutMethodIsRequired) {
    return {
      "create-business": "completed",
      "add-business-director": "completed",
      "add-bank-account": "current",
      "accept-terms": "upcoming",
    };
  }

  const termsOfServiceAcceptanceIsRequired =
    !account.termsOfService?.acceptance;

  if (termsOfServiceAcceptanceIsRequired) {
    return {
      "create-business": "completed",
      "add-business-director": "completed",
      "add-bank-account": "completed",
      "accept-terms": "current",
    };
  }

  return {
    "create-business": "completed",
    "add-business-director": "completed",
    "add-bank-account": "completed",
    "accept-terms": "completed",
  };
};
